/** Follow-up datetime pickers use 0 / 15 / 30 / 45 minute steps only. */
export const DATETIME_MINUTE_INTERVAL = 15;
export const DATETIME_LOCAL_STEP_SECONDS = DATETIME_MINUTE_INTERVAL * 60;
export const QUARTER_HOUR_MINUTE_OPTIONS = [0, 15, 30, 45];
export const HOUR_12_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function buildQuarterHourTimeSlots() {
  const slots = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (const minute of QUARTER_HOUR_MINUTE_OPTIONS) {
      slots.push(`${pad2(hour)}:${pad2(minute)}`);
    }
  }
  return slots;
}

/** Parse "HH:mm" (24h) into { hour12, minute, period }. */
export function splitTimeTo12Hour(time24) {
  const raw = typeof time24 === 'string' ? time24 : '';
  const [hPart, mPart] = raw.split(':');
  let hour24 = Number.parseInt(hPart, 10);
  let minute = Number.parseInt(mPart, 10);

  if (!Number.isFinite(hour24) || hour24 < 0 || hour24 > 23) {
    hour24 = 10;
  }
  if (!Number.isFinite(minute) || !QUARTER_HOUR_MINUTE_OPTIONS.includes(minute)) {
    const nearest = QUARTER_HOUR_MINUTE_OPTIONS.reduce((best, option) =>
      Math.abs(option - (Number.isFinite(minute) ? minute : 0)) < Math.abs(best - (Number.isFinite(minute) ? minute : 0))
        ? option
        : best
    );
    minute = nearest;
  }

  const period = hour24 >= 12 ? 'PM' : 'AM';
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;

  return { hour12, minute, period };
}

/** Build "HH:mm" (24h) from 12-hour parts. */
export function join12HourToTime(hour12, minute, period) {
  let h = Number.parseInt(hour12, 10);
  let m = Number.parseInt(minute, 10);
  const ampm = String(period || 'AM').toUpperCase() === 'PM' ? 'PM' : 'AM';

  if (!Number.isFinite(h) || h < 1 || h > 12) h = 10;
  if (!QUARTER_HOUR_MINUTE_OPTIONS.includes(m)) m = 0;

  let hour24 = h % 12;
  if (ampm === 'PM') hour24 += 12;

  return `${pad2(hour24)}:${pad2(m)}`;
}

export function splitDatetimeLocalValue(value) {
  if (value == null || value === '') {
    return { date: '', time: '10:00' };
  }

  const snapped = snapDatetimeLocalValue(value);
  const [date, time] = snapped.split('T');
  return { date: date || '', time: time || '10:00' };
}

export function joinDatetimeLocalValue(date, time) {
  if (!date || !time) {
    return '';
  }
  return snapDatetimeLocalValue(`${date}T${time}`);
}

export function snapDateToQuarterHour(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {
    return null;
  }

  const totalMinutes = d.getHours() * 60 + d.getMinutes();
  let snappedMinutes = Math.round(totalMinutes / DATETIME_MINUTE_INTERVAL) * DATETIME_MINUTE_INTERVAL;

  if (snappedMinutes >= 24 * 60) {
    d.setDate(d.getDate() + 1);
    snappedMinutes = 0;
  }

  d.setHours(Math.floor(snappedMinutes / 60), snappedMinutes % 60, 0, 0);
  return d;
}

export function toDatetimeLocalValue(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function snapDatetimeLocalValue(value) {
  if (value == null || value === '') {
    return '';
  }

  const snapped = snapDateToQuarterHour(new Date(value));
  return snapped ? toDatetimeLocalValue(snapped) : String(value);
}
